import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type { CustomerAccount } from "@wallyo/contracts/console/accounts";
import type { ListProSubscriptionsResponse } from "@wallyo/contracts/console/billing";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import {
  getCustomerAccount,
  listAuditLog,
  listProSubscriptions,
  revealContactDetails,
  searchAccounts,
} from "../../api/console-api";
import { useApi } from "../../api/context";
import { ConsoleApiError } from "../../api/errors";
import { t } from "../../i18n";
import { useHash } from "../../navigation/use-hash";
import { useAlerts } from "../../shell/alerts";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { PageTop } from "../../shell/PageTop";
import { useStaff } from "../../shell/staff-context";
import { INITIAL_AUDIT_LOG_STATE, auditLogReducer } from "../audit-log-state";
import { BILLING_FEATURES, type BillingTarget } from "../billing/billing-actions";
import type { BillingListState } from "../billing/billing-model";
import { BillingWriteDialog } from "../billing/BillingWriteDialog";
import { CustomerPageView } from "./CustomerPageView";
import { customerHref, parseCustomerHash, searchHref, type CustomerTab } from "./customer-route";
import { CustomerSearchView } from "./CustomerSearchView";
import { FeatureLock } from "./FeatureLock";
import { MASKED, revealReducer, type RevealEvent } from "./reveal-state";
import { INITIAL_SEARCH_STATE, queryProblem, searchReducer } from "./search-state";

const FEATURES = {
  search: "SEARCH_ACCOUNTS_STAFF_FEATURE_ENABLEMENT",
  view: "VIEW_ACCOUNTS_STAFF_FEATURE_ENABLEMENT",
  reveal: "REVEAL_CONTACT_DETAILS_STAFF_FEATURE_ENABLEMENT",
  audit: "VIEW_AUDIT_LOG_STAFF_FEATURE_ENABLEMENT",
  subscriptions: BILLING_FEATURES.subscriptions,
  discounts: BILLING_FEATURES.discounts,
} as const satisfies Record<string, StaffFeatureId>;

/**
 * Clients & pros. `#customer` is the search (SEARCH_ACCOUNTS),
 * `#customer/<userId>` a customer's page (VIEW_ACCOUNTS). The server checks
 * every feature again on every request; these only decide what to offer.
 */
export function CustomersScreen() {
  const hash = useHash();
  const { me } = useStaff();
  const route = parseCustomerHash(hash);
  const has = (feature: StaffFeatureId) => me.features.includes(feature);
  const roles = me.staffMember.roles.map((role) => role.name);

  if (route.kind === "search") {
    return (
      <CustomerSearch query={route.query} canSearch={has(FEATURES.search)} canView={has(FEATURES.view)} roles={roles} />
    );
  }
  if (!has(FEATURES.view)) {
    return (
      <>
        <PageTop title={t("screen.customers")} sub={t("sub.customers")} />
        <FeatureLock lead={t("customers.page.locked")} feature={FEATURES.view} roles={roles} />
      </>
    );
  }
  // Keyed by the customer: another customer's page starts from nothing, contact details masked.
  return (
    <CustomerPage
      key={route.userId}
      userId={route.userId}
      tab={route.tab}
      canReveal={has(FEATURES.reveal)}
      canViewAudit={has(FEATURES.audit)}
      canManageSubscriptions={has(FEATURES.subscriptions)}
      canOfferDiscounts={has(FEATURES.discounts)}
      roles={roles}
    />
  );
}

// ── Search ───────────────────────────────────────────────────────────────

function CustomerSearch({
  query,
  canSearch,
  canView,
  roles,
}: {
  query: string;
  canSearch: boolean;
  canView: boolean;
  roles: string[];
}) {
  const { client } = useApi();
  const [state, dispatch] = useReducer(searchReducer, INITIAL_SEARCH_STATE);
  const [reloads, reload] = useReducer((count: number) => count + 1, 0);
  // Answers for a query no longer on screen are dropped.
  const generation = useRef(0);
  const trimmed = query.trim();

  useEffect(() => {
    const mine = ++generation.current;
    if (!canSearch || queryProblem(trimmed)) {
      dispatch({ type: "idle" });
      return;
    }
    dispatch({ type: "search" });
    searchAccounts(client, { query: trimmed }).then(
      (page) => {
        if (generation.current === mine) dispatch({ type: "loaded", page });
      },
      (error: unknown) => {
        if (generation.current === mine) dispatch({ type: "failed", error });
      },
    );
  }, [client, trimmed, canSearch, reloads]);

  const loadMore = useCallback(() => {
    if (!state.nextCursor || state.loadingMore) return;
    const mine = generation.current;
    dispatch({ type: "loadMore" });
    searchAccounts(client, { query: trimmed, cursor: state.nextCursor }).then(
      (page) => {
        if (generation.current === mine) dispatch({ type: "loadedMore", page });
      },
      (error: unknown) => {
        if (generation.current === mine) dispatch({ type: "moreFailed", error });
      },
    );
  }, [client, trimmed, state.nextCursor, state.loadingMore]);

  const onSearch = useCallback(
    (next: string) => {
      // The same query again is a retry: the hash wouldn't change.
      if (next === trimmed) reload();
      else window.location.hash = searchHref(next);
    },
    [trimmed],
  );

  return (
    <CustomerSearchView
      query={query}
      state={state}
      canSearch={canSearch}
      canView={canView}
      roles={roles}
      onSearch={onSearch}
      onRetry={reload}
      onLoadMore={loadMore}
    />
  );
}

// ── A customer's page ────────────────────────────────────────────────────

type PageState =
  | { status: "loading" }
  | { status: "ready"; account: CustomerAccount }
  | { status: "failed"; error: unknown };

const LOADING = { status: "loading", data: null } as const;

/** The pro profile billing keys a subscription by; null for a client. */
function proProfileIdOf(state: PageState): string | null {
  if (state.status !== "ready") return null;
  return state.account.proProfile?.proProfileId ?? null;
}

function CustomerPage({
  userId,
  tab,
  canReveal,
  canViewAudit,
  canManageSubscriptions,
  canOfferDiscounts,
  roles,
}: {
  userId: string;
  tab: CustomerTab;
  canReveal: boolean;
  canViewAudit: boolean;
  canManageSubscriptions: boolean;
  canOfferDiscounts: boolean;
  roles: string[];
}) {
  const { client } = useApi();
  const [state, setState] = useState<PageState>({ status: "loading" });
  const [reloads, reload] = useReducer((count: number) => count + 1, 0);
  // The revealed details live here and only here: gone when this page unmounts.
  const [reveal, dispatchReveal] = useReducer(revealReducer, MASKED);
  const [journal, dispatchJournal] = useReducer(auditLogReducer, INITIAL_AUDIT_LOG_STATE);
  const [journalReads, readJournal] = useReducer((count: number) => count + 1, 0);
  const journalGeneration = useRef(0);
  const journalWanted = tab === "audit" && canViewAudit;
  // Which read of the journal is on screen: it is read when its tab first opens, on a retry,
  // and again after a reveal adds to it. Reading it is itself audited, so not on every tab switch.
  const journalShown = useRef(-1);
  const inFlight = useRef(false);

  useEffect(() => {
    let current = true;
    setState({ status: "loading" });
    getCustomerAccount(client, userId).then(
      ({ account }) => current && setState({ status: "ready", account }),
      (error: unknown) => current && setState({ status: "failed", error }),
    );
    return () => {
      current = false;
    };
  }, [client, userId, reloads]);

  useEffect(() => {
    if (!journalWanted || journalShown.current === journalReads) return;
    journalShown.current = journalReads;
    const mine = ++journalGeneration.current;
    dispatchJournal({ type: "reload" });
    listAuditLog(client, { subjectUserId: userId }).then(
      (page) => {
        if (journalGeneration.current === mine) dispatchJournal({ type: "loaded", page });
      },
      (error: unknown) => {
        if (journalGeneration.current === mine) dispatchJournal({ type: "failed", error });
      },
    );
  }, [client, userId, journalWanted, journalReads]);

  const journalMore = useCallback(() => {
    if (!journal.nextCursor || journal.loadingMore) return;
    const mine = journalGeneration.current;
    dispatchJournal({ type: "loadMore" });
    listAuditLog(client, { subjectUserId: userId, cursor: journal.nextCursor }).then(
      (page) => {
        if (journalGeneration.current === mine) dispatchJournal({ type: "loadedMore", page });
      },
      (error: unknown) => {
        if (journalGeneration.current === mine) dispatchJournal({ type: "moreFailed", error });
      },
    );
  }, [client, userId, journal.nextCursor, journal.loadingMore]);

  const onReveal = useCallback(
    (event: RevealEvent) => {
      if (event.type !== "submit") {
        dispatchReveal(event);
        return;
      }
      // Never optimistic: the details appear only from the server's answer.
      // One click, one request: a second click while it is on its way does nothing.
      if (inFlight.current) return;
      const next = revealReducer(reveal, event);
      dispatchReveal(event);
      if (next.status !== "sending") return;
      inFlight.current = true;
      revealContactDetails(client, userId, { reason: next.reason.trim() })
        .then(
          (details) => {
            dispatchReveal({ type: "succeeded", details });
            readJournal();
          },
          (error: unknown) => dispatchReveal({ type: "failed", error }),
        )
        .finally(() => {
          inFlight.current = false;
        });
    },
    [client, userId, reveal],
  );

  const onTab = useCallback(
    (next: CustomerTab) => {
      window.location.hash = customerHref(userId, next);
    },
    [userId],
  );

  // Billing's view of this pro, read when the Abonnement tab opens.
  const [subscriptions, setSubscriptions] = useState<BillingListState<ListProSubscriptionsResponse>>(LOADING);
  const [subscriptionReads, readSubscriptions] = useReducer((count: number) => count + 1, 0);
  const [billingTarget, setBillingTarget] = useState<BillingTarget | null>(null);
  const alerts = useAlerts();
  const workerId = proProfileIdOf(state);
  const wantsSubscriptions = tab === "subscription" && workerId !== null;

  useEffect(() => {
    if (!wantsSubscriptions || !workerId) return;
    let current = true;
    setSubscriptions((was) => ({ status: "loading", data: was.data }));
    listProSubscriptions(client, workerId).then(
      (data) => current && setSubscriptions({ status: "ready", data }),
      (error: unknown) => current && setSubscriptions((was) => ({ status: "failed", data: was.data, error })),
    );
    return () => {
      current = false;
    };
  }, [client, workerId, wantsSubscriptions, subscriptionReads]);

  const openBilling = (next: BillingTarget) => setBillingTarget(next);

  if (state.status !== "ready") {
    return (
      <>
        <PageTop title={t("screen.customers")} sub={t("sub.customers")} />
        <div className="crumbs">
          <a href="#customer">{t("customers.back")}</a>
        </div>
        {state.status === "loading" ? (
          <div className="empty" role="status">
            <span className="spinner" aria-hidden="true" />
            <span>{t("customers.page.loading")}</span>
          </div>
        ) : (
          <div className="audit-error">
            <p className="foot">
              {state.error instanceof ConsoleApiError && state.error.status === 404
                ? t("customers.page.notFound")
                : t("customers.page.loadFailed")}
            </p>
            <ErrorNotice error={state.error} />
            <div>
              <button className="btn primary" type="button" onClick={reload}>
                {t("audit.retry")}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <CustomerPageView
        account={state.account}
        tab={tab}
        reveal={reveal}
        canReveal={canReveal}
        canViewAudit={canViewAudit}
        journal={journal}
        subscriptions={{
          state: subscriptions,
          canManage: canManageSubscriptions,
          canOfferDiscounts,
          onExtend: (subscription) => openBilling({ kind: "extendSubscription", subscription }),
          onEndNow: (subscription) => openBilling({ kind: "endSubscription", subscription }),
          onRetry: readSubscriptions,
        }}
        roles={roles}
        onTab={onTab}
        onReveal={onReveal}
        onJournalRetry={readJournal}
        onJournalMore={journalMore}
      />
      <BillingWriteDialog
        target={billingTarget}
        plans={[]}
        workerId={null}
        canViewAccounts
        onClose={() => setBillingTarget(null)}
        onReload={() => {
          setBillingTarget(null);
          readSubscriptions();
        }}
        onDone={(done) => {
          setBillingTarget(null);
          alerts.ok(done.text);
          readSubscriptions();
          // The pro's header line (plan, paid until) comes from the account.
          reload();
        }}
      />
    </>
  );
}
